import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { useServerConfig } from '../../hooks/useServerConfig';
import logger from '../../utils/logging/logger';
import styles from './PaymentForm.module.css';
import { STRIPE_APPEARANCE } from './paymentForm/paymentFormConstants';
import OrderSummary from './paymentForm/OrderSummary';
import GuestDetailsCard from './paymentForm/GuestDetailsCard';
import { useGuestPayment } from './paymentForm/paymentApiContext';
import PaymentSection from './paymentForm/PaymentSection';
import { PaymentLoadingScreen, PaymentNotFoundScreen, PaymentSuccessScreen } from './paymentForm/PaymentStatusScreens';
import { usePaymentForm } from './paymentForm/usePaymentForm';
import { CHECKOUT_NOTICE_KEY } from './paymentForm/usePaymentForm/createPaymentIntentHelpers';

// Payment form component
function PaymentFormContent(props) {
  const {
    appliedPromo,
    applyPromoCode,
    bulkPricing,
    clientSecret,
    courseId,
    courseIds,
    courses,
    creatingPaymentIntent,
    elements,
    error,
    handleProceedToPayment,
    handleSubmit,
    loading,
    navigate,
    paymentIntentCreated,
    processing,
    productIds,
    promoCode,
    promoError,
    promoInfo,
    promoLoading,
    removeBulkPricing,
    removePromoCode,
    setError,
    setProcessing,
    setPromoCode,
    setPromoError,
    setPromoInfo,
    setShowCardDivider,
    setSuccess,
    showCardDivider,
    showPaymentForm,
    stripe,
    success,
    topAnchorRef,
    totalPrice,
  } = usePaymentForm(props);

  // After the order was changed for the student (e.g. a course they already own was dropped), show why, once.
  const [checkoutNotice, setCheckoutNotice] = useState(null);
  useEffect(() => {
    try {
      const message = sessionStorage.getItem(CHECKOUT_NOTICE_KEY);
      if (message) {
        setCheckoutNotice(message);
        sessionStorage.removeItem(CHECKOUT_NOTICE_KEY);
      }
    } catch (storageError) {
      // No notice.
    }
  }, [courseIds]);

  // New students: check the details card first (problems show on the card), then proceed.
  const guest = useGuestPayment();
  const onProceed = guest
    ? () => { if (guest.validateDetails()) handleProceedToPayment(); }
    : handleProceedToPayment;

  // FIX #36: Move logger calls out of render to prevent unnecessary re-renders
  // Logger calls moved to useEffect hooks where appropriate

  if (loading) {
    return <PaymentLoadingScreen />;
  }

  // FIX #21 & #30: Better handling of empty courses array edge case
  // FIX: Add null safety check and prevent render errors
  if (!courses || !Array.isArray(courses) || courses.length === 0) {
    return (
      <PaymentNotFoundScreen
        courses={courses}
        error={error}
        productIds={productIds}
        courseId={courseId}
        courseIds={courseIds}
        loading={loading}
        navigate={navigate}
      />
    );
  }

  if (success) {
    return <PaymentSuccessScreen productIds={productIds} courses={courses} />;
  }

  return (
    <div className={styles.container}>
      <motion.div
        className={styles.paymentCard}
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div ref={topAnchorRef} className={styles.backButtonContainer}>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/catalog')}
            aria-label="Back to course catalog"
            type="button"
          >
            <FiArrowLeft size={16} />
            Back to Course Catalog
          </button>
        </div>

        {checkoutNotice && (
          <div className={styles.checkoutNotice} role="status">{checkoutNotice}</div>
        )}

        <div className={styles.header}>
          <h1 className={styles.title}>{productIds ? 'Product Purchase' : 'Complete Your Enrollment'}</h1>
          <p className={styles.subtitle}>Complete your payment to {productIds ? 'purchase' : 'enroll in'} {courses && courses.length > 0 ? courses.length : 0} {productIds ? 'product' : 'course'}{(courses && courses.length !== 1) ? 's' : ''}</p>
        </div>

        {/* New students only (renders nothing when signed in) */}
        <GuestDetailsCard locked={paymentIntentCreated} />

        <OrderSummary
          appliedPromo={appliedPromo}
          applyPromoCode={applyPromoCode}
          bulkPricing={bulkPricing}
          courses={courses}
          error={error}
          productIds={productIds}
          promoCode={promoCode}
          promoError={promoError}
          promoInfo={promoInfo}
          promoLoading={promoLoading}
          removeBulkPricing={removeBulkPricing}
          removePromoCode={removePromoCode}
          setPromoCode={setPromoCode}
          setPromoError={setPromoError}
          setPromoInfo={setPromoInfo}
          totalPrice={totalPrice}
        />

        {/* Show "Proceed to Payment" or "Complete Enrollment" button if payment form is not shown yet */}
        <PaymentSection
          clientSecret={clientSecret}
          courses={courses}
          creatingPaymentIntent={creatingPaymentIntent}
          elements={elements}
          handleProceedToPayment={onProceed}
          handleSubmit={handleSubmit}
          navigate={navigate}
          paymentIntentCreated={paymentIntentCreated}
          processing={processing}
          productIds={productIds}
          setError={setError}
          setProcessing={setProcessing}
          setShowCardDivider={setShowCardDivider}
          setSuccess={setSuccess}
          showCardDivider={showCardDivider}
          showPaymentForm={showPaymentForm}
          stripe={stripe}
          success={success}
          totalPrice={totalPrice}
        />
      </motion.div>
    </div>
  );
}

// Main payment form component with Stripe Elements
function PaymentForm() {
  const { config, loading, error } = useServerConfig();
  const [clientSecret, setClientSecret] = useState(null);
  // FIX: Preserve promo code state in parent to survive Elements remounts
  const [preservedAppliedPromo, setPreservedAppliedPromo] = useState(null);
  // FIX: Track if we've ever had a clientSecret to create a stable key
  // Only remount Elements when transitioning from no-secret to secret (or vice versa)
  // This prevents remount loops when clientSecret is updated with the same value
  const hasHadClientSecretRef = useRef(false);
  
  // FIX: Use ref to store stripe promise to prevent recreation and "stripe prop change" error
  // Stripe Elements doesn't allow changing the stripe prop after it's been set
  // Note: Frontend Stripe.js SDK automatically uses the latest compatible version
  // The backend API version (2025-10-29.clover) is configured in server/config/stripe.js
  // and applies to all backend API calls
  // FIX: Use useMemo to create stripe promise - same pattern as DonationForm
  // This ensures the promise is stable and only recreates when config changes
  // Extract key to avoid dependency on entire config object
  const stripeKey = config?.stripePublishableKey;
  const stripePromise = useMemo(() => {
    if (!stripeKey) return null;
    
    try {
      return loadStripe(stripeKey);
    } catch (stripeError) {
      logger.error('Failed to load Stripe', { error: stripeError, hasKey: !!stripeKey });
      return null;
    }
  }, [stripeKey]); // Only depend on the key value, not entire config object

  // FIX: Track clientSecret transitions in useEffect to avoid modifying refs during render
  useEffect(() => {
    if (clientSecret && !hasHadClientSecretRef.current) {
      hasHadClientSecretRef.current = true;
    }
  }, [clientSecret]);

  // FIX: Use clientSecret as key to ensure Elements remounts when payment intent changes
  // This is critical for ensuring Stripe Elements uses the correct payment intent amount
  // when promo codes are applied and payment intents are recreated
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const elementsKey = useMemo(() => {
    // Use clientSecret as key to force remount when it changes
    // This ensures Stripe Elements always uses the latest payment intent
    if (clientSecret) {
      // Extract payment intent ID from clientSecret for stable key
      // Format: pi_xxx_secret_yyy -> use pi_xxx as key
      const match = clientSecret.match(/^(pi_[^_]+)/);
      if (match) {
        return `payment-intent-${match[1]}`;
      }
      // Fallback: use clientSecret hash or full value (less stable but works)
      return `payment-intent-${clientSecret.substring(0, 20)}`;
    } else {
      // No secret - initial state
      return 'init';
    }
  }, [clientSecret]); // Remount when clientSecret changes (including on forceRecreate)

  const handleClientSecretChange = useCallback((newClientSecret) => {
    logger.debug('Client secret changed', { hasValue: !!newClientSecret, current: !!clientSecret, isSame: newClientSecret === clientSecret });
    // FIX: Only update if value actually changed to prevent unnecessary remounts
    // CRITICAL: This prevents remount loops - only update if it's actually a different value
    if (newClientSecret !== clientSecret) {
      setClientSecret(newClientSecret);
    } else {
      logger.debug('Skipping clientSecret update - value unchanged', { hasValue: !!newClientSecret });
    }
  }, [clientSecret]);

  // FIX: Memoize appearance object to prevent Stripe Elements from thinking options changed
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const appearance = useMemo(() => STRIPE_APPEARANCE, []);

  // FIX: Don't initialize Stripe Elements with null clientSecret - it causes IntegrationError
  // Only render Elements when we have a valid clientSecret or when we're ready to create one
  // Pass undefined instead of null to avoid Stripe validation errors
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const elementsOptions = useMemo(() => {
    // Only include clientSecret if it's not null
    // NOTE: When using clientSecret, the paymentMethodConfiguration must be set on the
    // PaymentIntent on the backend (via payment_method_configuration parameter), not here.
    const options = { appearance };
    if (clientSecret) {
      options.clientSecret = clientSecret;
    }
    return options;
  }, [clientSecret, appearance]);

  if (loading) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment configuration...
          </div>
        </motion.div>
      </div>
    );
  }

  if (error || !config || !config.stripePublishableKey) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={styles.errorMessage}
            role="alert"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              Stripe is not properly configured. Please check your server configuration.
              {error && <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>Error: {error}</div>}
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // FIX #7: Enhanced error handling for Stripe load failure
  if (!stripePromise) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={styles.errorMessage}
            role="alert"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              {error 
                ? `Payment system configuration error: ${error}. Please contact support.`
                : 'Stripe payment system is not available. Please refresh the page or contact support if the problem persists.'
              }
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // FIX #36: Removed logger.debug from render - use useEffect for logging if needed

  // FIX: Always render Elements wrapper (required for useStripe/useElements hooks)
  // But only include clientSecret in options when it's available (not null)
  // This prevents the "clientSecret should be a client_secret string. You specified: null" error
  return (
    <Elements key={elementsKey} stripe={stripePromise} options={elementsOptions}>
      <PaymentFormContent
        onClientSecretChange={handleClientSecretChange}
        clientSecretFromParent={clientSecret}
        preservedAppliedPromo={preservedAppliedPromo}
        setPreservedAppliedPromo={setPreservedAppliedPromo}
        stripeConfig={config}
      />
    </Elements>
  );
}

export default PaymentForm;
